import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { UserContext } from "../../hooks/userContext";
import { debounce, InputAdornment, TextField } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DataGrid } from "@mui/x-data-grid";
import moment, { Moment } from "moment";
import { useQueryHistory } from "../../queries/order/hooks/useQueryHistory";
import { OrderUserType } from "../../constants/order";
import { HistoryDialog } from "../../components/shared/HistoryDialog/HistoryDialog";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import {
  DEFAULT_HISTORY_DATE_RANGE,
  getHistoryDateRange,
  HistoryDateRangePreset,
} from "../historyDateRange";
import { HistoryDateRangeFilter } from "../HistoryDateRangeFilter";
import { pageTitleSx } from "../../constants/designTokens";
import {
  getHistoryGridColumns,
  historyDataGridSx,
  HistoryGridRow,
  mapOrdersToHistoryGridRows,
} from "../historyGrid";
import { BrandLoaderOverlay } from "../../components/shared/BrandLoader/BrandLoader";

interface IBuyHistoryProps {
  isAdmin?: boolean;
}

export const BuyHistory: React.FunctionComponent<IBuyHistoryProps> = ({ isAdmin }) => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const [dateRange, setDateRange] = useState<HistoryDateRangePreset>(DEFAULT_HISTORY_DATE_RANGE);
  const [{ fromDate, toDate }, setDates] = useState(() => getHistoryDateRange(DEFAULT_HISTORY_DATE_RANGE));
  const [searchInput, setSearchInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<any>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const handlePresetChange = (preset: Exclude<HistoryDateRangePreset, "custom">) => {
    setDateRange(preset);
    setDates(getHistoryDateRange(preset));
  };

  const handleFromDateChange = (date: Moment | null) => {
    if (!date) return;
    setDateRange("custom");
    setDates((prev) => ({ ...prev, fromDate: date }));
  };

  const handleToDateChange = (date: Moment | null) => {
    if (!date) return;
    setDateRange("custom");
    setDates((prev) => ({ ...prev, toDate: date }));
  };

  const buildWhere = useCallback(
    (searchValue: string) => {
      const andQuery: any[] = isAdmin ? [] : [{ buyerUserId: { equals: +id } }];

      if (searchValue) {
        if (isAdmin) {
          andQuery.push({
            OR: [
              { orderNumber: { contains: searchValue, mode: "insensitive" } },
              { Seller: { is: { legalName: { contains: searchValue, mode: "insensitive" } } } },
            ],
          });
        } else {
          andQuery.push({ orderNumber: { contains: searchValue, mode: "insensitive" } });
        }
      }

      if (fromDate) {
        const dateStr = fromDate.format("YYYY-MM-DD");
        andQuery.push({
          createdAt: { gte: moment(`${dateStr} 00:00:00`).utc().format("YYYY-MM-DD HH:mm:ss") },
        });
      }

      if (toDate) {
        const dateStr = toDate.format("YYYY-MM-DD");
        andQuery.push({
          createdAt: { lte: moment(`${dateStr} 23:59:59`).utc().format("YYYY-MM-DD HH:mm:ss") },
        });
      }

      return { AND: andQuery };
    },
    [fromDate, toDate, id, isAdmin]
  );

  const [getHistory, { data, loading: queryLoading, refetch }] = useQueryHistory({
    variables: {
      where: buildWhere(""),
      orderBy: [{ createdAt: "desc" }],
    },
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  useEffect(() => {
    getHistory({
      variables: {
        where: buildWhere(searchInput),
        orderBy: [{ createdAt: "desc" }],
      },
      fetchPolicy: "no-cache",
      pollInterval: 5000,
    });
  }, [buildWhere, getHistory, searchInput]);

  const search = useCallback(
    debounce(async (value: string) => {
      await refetch({
        where: buildWhere(value),
        orderBy: [{ createdAt: "desc" }],
      });
    }, 400),
    [buildWhere, refetch]
  );

  const handleSearch = (value: string) => {
    setSearchInput(value);
    setPaginationModel((model) => ({ ...model, page: 0 }));
    search(value);
  };

  const rows = useMemo(
    () => mapOrdersToHistoryGridRows(data?.findManyOrder, "buyer"),
    [data?.findManyOrder]
  );

  const columns = useMemo(
    () =>
      getHistoryGridColumns({
        mode: "buyer",
        isAdmin,
        onCopy: (value) => {
          navigator.clipboard.writeText(value);
          addToast({ type: NotificationType.Success, title: "Copied to Clipboard" });
        },
        refetch,
        refetchLoading: queryLoading,
      }),
    [isAdmin, addToast, refetch, queryLoading]
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "14px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: '"Source Sans 3", sans-serif',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 0.75,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E2E8F0",
            minHeight: 44,
          }}
        >
          <Typography component="h1" sx={pageTitleSx}>
            {isAdmin ? "Sales" : "Buy History"}
          </Typography>
          <TextField
            type="text"
            placeholder={`Search by ${isAdmin ? "Seller or " : ""}Order Number…`}
            value={searchInput}
            onChange={(event) => handleSearch(event.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: '"Source Sans 3", sans-serif',
                fontSize: 14,
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#475569" },
                "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1.5px" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "#475569" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ px: 2.5, borderBottom: "1px solid #E2E8F0" }}>
          <HistoryDateRangeFilter
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onPresetChange={handlePresetChange}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <DataGrid
            loading={queryLoading}
            rows={rows}
            columns={columns}
            rowHeight={48}
            columnHeaderHeight={40}
            autoPageSize
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[paginationModel.pageSize]}
            disableRowSelectionOnClick
            onRowClick={(params) => {
              setSelectedOrders((params.row as HistoryGridRow).orders);
              setDialogOpen(true);
            }}
            sx={{ ...historyDataGridSx, height: "100%" }}
            localeText={{ noRowsLabel: "No orders found" }}
            slots={{ loadingOverlay: BrandLoaderOverlay }}
          />
        </Box>
      </Box>

      {dialogOpen && (
        <HistoryDialog
          orders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          open={dialogOpen}
          setOpen={setDialogOpen}
          user={OrderUserType.buyer}
        />
      )}
    </Box>
  );
};

export default BuyHistory;
